---
meta:
  - title: Usar clientes en el redactor - IUSPRO Docs
  - description: Aprenda cuándo conviene redactar dentro de un cliente y qué contexto usa el Redactor.
---

# Usar clientes en el redactor

El Redactor puede trabajar dentro de un cliente.

Esto es útil cuando el documento que va a redactar forma parte del trabajo de un cliente y usted quiere reutilizar el contexto del cliente en vez de volver a explicarlo todo desde cero.

## Qué toma el Redactor del cliente

Si crea un documento dentro de un cliente, el Redactor puede aprovechar:

- las instrucciones del cliente
- los expedientes vinculados al cliente
- los archivos adjuntos del cliente
- la orientación general de ese espacio de trabajo

En otras palabras, el cliente funciona como una base de contexto para la redacción.

## Cuándo conviene usar el Redactor dentro de un cliente

Conviene especialmente cuando:

- está redactando varios documentos para el mismo cliente
- ya tiene expedientes y archivos organizados en un cliente
- quiere mantener una línea de trabajo constante
- no quiere repetir el contexto en cada documento nuevo

Ejemplos:

- una demanda y luego su contestación
- varios escritos de trámite sobre el mismo caso
- correcciones sucesivas del mismo documento
- documentos distintos que dependen del mismo expediente y los mismos archivos

## Cómo crear un documento en un cliente

Puede hacerlo de varias formas:

1. Entrando al Redactor y eligiendo un cliente al iniciar el documento
2. Entrando desde un chat que ya pertenece a un cliente
3. Entrando desde la página del propio cliente cuando detecta que quiere redactar

En esos casos, el documento queda asociado a ese cliente desde el inicio.

## Qué ventaja tiene eso

La ventaja principal es que el Redactor no trabaja “en vacío”.

En vez de partir solo de una instrucción corta, también puede usar el contexto acumulado del cliente para:

- entender mejor el asunto
- recuperar información útil de los archivos
- apoyarse en expedientes ya vinculados
- mantener coherencia entre distintos documentos del mismo tema

## Importante: el cliente no reemplaza su instrucción

Aunque el documento use el contexto del cliente, su instrucción actual sigue siendo clave.

La mejor práctica es:

- tener un cliente bien organizado
- y además decir claramente qué documento quiere y para qué

Por ejemplo:

```text
Redacta una contestación de demanda breve, con énfasis en falta de prueba del monto y reserva de excepciones.
```

El cliente aporta el contexto.
Su instrucción define la tarea concreta.

## Si el cliente está compartido

Si otra persona le compartió un cliente y usted puede usarlo, también puede crear sus propios documentos y chats relacionados con ese cliente.

Eso no significa que vea los chats de las demás personas.

La lógica sigue siendo:

- **el contexto del cliente es compartido**
- **sus chats y su trabajo concreto siguen siendo suyos**

## Cuándo NO hace falta usar un cliente

No hace falta si:

- solo va a redactar un documento aislado
- no necesita archivos o expedientes adicionales
- el trabajo no va a continuar después

En esos casos, puede usar el Redactor directamente sin cliente.

## Recomendación práctica

Use esta regla simple:

- **documento aislado**: Redactor sin cliente
- **documento dentro de un trabajo continuo**: Redactor con cliente

Así aprovecha mejor el contexto y mantiene más ordenado el trabajo.

## Siguiente lectura

- Lea [cómo usar clientes](/guias/proyectos-como-usar)
- Lea [compartir clientes](/guias/proyectos-compartir-proyectos)
- Vea [cómo usar el Redactor](/guias/redactor-como-usar)
